import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { Icon } from '@iconify/react';
import { fileService } from "@/services/upload/file.service";
import { useServeFileById } from '@/hooks/upload/use-file';
import {
  ImagePreview,
  PdfPreview,
  DocumentPreview,
  SpreadsheetPreview,
  PresentationPreview,
  AudioPreview,
  VideoPreview
} from '../form-answer/previews';
import { QuestionTypes } from "@/types";

interface UploadResponseProps {
  response: any;
  label: string;
  question?: QuestionTypes; 
}

const UploadResponse: React.FC<UploadResponseProps> = ({ response, label, question }) => {
  const { t } = useTranslation();
  
  // Use the same hook as upload-answer to get file content for preview
  const { 
    data: fileUrl, 
    isLoading: isLoadingFile, 
    error: fileError 
  } = useServeFileById(response || '');

  const renderFilePreview = () => {
    if (isLoadingFile) {
      return (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
          <div className="flex items-center justify-center h-32">
            <div className="flex items-center space-x-2 text-gray-500">
              <Icon icon="mdi:loading" className="animate-spin text-lg" />
              <span>Loading preview...</span>
            </div>
          </div>
        </div>
      );
    }

    if (fileError || !fileUrl) {
      return (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-gray-500">
              <Icon icon="mdi:file-alert" className="text-2xl mx-auto mb-2" />
              <p className="text-sm">Failed to load preview</p>
            </div>
          </div>
        </div>
      );
    }

    const previewProps = { fileUrl, fileName: response || 'Unknown file' };

    // Use question.documentType exactly like upload-answer
    switch (question?.documentType) {
      case 'image':
        return <ImagePreview {...previewProps} />;
      case 'pdf':
        return <PdfPreview {...previewProps} />;
      case 'document':
        return <DocumentPreview {...previewProps} />;
      case 'spreadsheet':
        return <SpreadsheetPreview {...previewProps} />;
      case 'presentation':
        return <PresentationPreview {...previewProps} />;
      case 'audio':
        return <AudioPreview {...previewProps} />;
      case 'video':
        return <VideoPreview {...previewProps} />;
      default:
        return (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
            <div className="flex items-center space-x-3">
              <Icon icon="mdi:file" className="text-4xl text-gray-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate" title={response || undefined}>
                  {response}
                </p>
                <p className="text-xs text-gray-500">File</p>
              </div>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors"
              >
                {t("formResponses.downloadFile")}
              </a>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="mb-2">
      {renderFilePreview()}
    </div>
  );
};

export default UploadResponse;
